<template>
  <v-container fluid class="pa-4 pa-md-6">
    <router-link :to="{ name: 'listing-refresh' }" class="text-caption">
      {{ $t("listing_refresh.back") }}
    </router-link>
    <div v-if="detail" class="d-flex align-center justify-space-between mb-4 mt-2">
      <div>
        <div class="text-h5">{{ detail.title }}</div>
        <v-chip size="small" class="mr-2" :color="statusColor(detail.status)">
          {{ statusLabel(detail) }}
        </v-chip>
        <span class="text-caption">{{ (detail.sites || []).join(", ") }}</span>
      </div>
      <div>
        <v-btn class="mr-2" variant="outlined" :loading="saving" @click="setPaused(!detail.paused)">
          {{ detail.paused ? $t("listing_refresh.resume") : $t("listing_refresh.pause") }}
        </v-btn>
        <v-btn color="red-darken-3" :loading="indexing" @click="runIndex">
          {{ $t("listing_refresh.run_indexer") }}
        </v-btn>
      </div>
    </div>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="indexResult" type="info" variant="tonal" class="mb-4">{{ indexResult }}</v-alert>

    <v-row v-if="detail">
      <v-col cols="12" md="8">
        <v-row>
          <v-col cols="12" md="6">
            <v-card class="pa-4">
              <div class="text-caption">{{ $t("listing_refresh.backlog") }}</div>
              <div class="text-h4">{{ progress }}%</div>
              <div class="text-caption">
                {{ doneRows }} {{ $t("listing_refresh.refreshed") }} ·
                {{ waitingRows }} {{ $t("listing_refresh.queued") }} ·
                {{ skippedRows }} {{ $t("listing_refresh.skipped") }}
              </div>
              <div class="text-caption">
                {{ $t("listing_refresh.queue_parts") }}: {{ detail.queueParts }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card class="pa-4">
              <div class="text-caption">{{ $t("listing_refresh.today") }}</div>
              <div class="text-h4">{{ detail.daily.done }} / {{ detail.dailyCap }}</div>
              <div class="text-caption">
                {{ $t("listing_refresh.attempted") }} {{ detail.daily.attempted }} ·
                {{ $t("listing_refresh.errors") }} {{ detail.daily.errors }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-card class="pa-4 mt-4">
          <div class="text-subtitle-1 mb-3">{{ $t("listing_refresh.last_14_days") }}</div>
          <div class="d-flex align-end" style="height: 140px; gap: 6px;">
            <div
              v-for="day in detail.days"
              :key="day.day"
              class="d-flex flex-column justify-end"
              style="flex: 1; height: 100%;"
            >
              <div
                :style="{ height: barHeight(day.refreshed) + '%', background: '#1e293b', minHeight: day.refreshed ? '2px' : '0' }"
              />
              <div
                :style="{ height: barHeight(day.errors) + '%', background: '#ea580c', minHeight: day.errors ? '2px' : '0' }"
              />
              <div class="text-caption text-center">{{ day.day.slice(8) }}</div>
            </div>
          </div>
        </v-card>

        <v-card class="pa-4 mt-4">
          <div class="d-flex justify-space-between mb-2">
            <div class="text-subtitle-1">{{ $t("listing_refresh.outcomes") }}</div>
            <v-btn-toggle v-model="outcomeRange" mandatory density="compact">
              <v-btn value="today">{{ $t("listing_refresh.today") }}</v-btn>
              <v-btn value="week">{{ $t("listing_refresh.seven_days") }}</v-btn>
              <v-btn value="allTime">{{ $t("listing_refresh.all_time") }}</v-btn>
            </v-btn-toggle>
          </div>
          <div v-for="status in outcomeOrder" :key="status" class="d-flex justify-space-between py-1">
            <span>{{ outcomeLabel(status) }}</span>
            <strong>{{ selectedOutcomes[status] || 0 }}</strong>
          </div>
        </v-card>

        <v-card class="mt-4">
          <div class="text-subtitle-1 pa-4">{{ $t("listing_refresh.recent_attempts") }}</div>
          <v-data-table :headers="attemptHeaders" :items="detail.attempts" item-value="id">
            <template #item.part="{ item }">
              <div>{{ item.partTitle || item.partId }}</div>
              <div class="text-caption">{{ item.partId }}</div>
            </template>
            <template #item.oldItemIds="{ item }">
              {{ item.oldItemIds[0] || "—" }}
              <span v-if="item.oldItemIds.length > 1" class="text-caption">
                +{{ item.oldItemIds.length - 1 }}
              </span>
            </template>
            <template #item.newItemIds="{ item }">
              {{ item.newItemIds[0] || "—" }}
              <span v-if="item.newItemIds.length > 1" class="text-caption">
                +{{ item.newItemIds.length - 1 }}
              </span>
            </template>
            <template #item.status="{ item }">
              <v-chip size="small">{{ outcomeLabel(item.status) }}</v-chip>
            </template>
            <template #item.createdAt="{ item }">
              {{ formatTime(item.createdAt) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="pa-4 mb-4">
          <div class="text-subtitle-1 mb-2">{{ $t("listing_refresh.settings") }}</div>
          <v-switch
            v-model="form.enabled"
            :label="$t('listing_refresh.enabled')"
            color="success"
            hide-details
            class="mb-2"
          />
          <v-alert
            v-if="!form.dryRun"
            type="success"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            {{ $t("listing_refresh.live_hint") }}
            <v-btn size="small" variant="text" @click="form.dryRun = true">
              {{ $t("listing_refresh.back_to_dry_run") }}
            </v-btn>
          </v-alert>
          <v-btn
            v-else
            size="small"
            variant="tonal"
            class="mb-3"
            @click="form.dryRun = false"
          >
            {{ $t("listing_refresh.go_live") }}
          </v-btn>
          <v-text-field
            v-model.number="form.dailyCap"
            type="number"
            :label="$t('listing_refresh.daily_cap')"
            variant="outlined"
            density="compact"
          />
          <v-text-field
            v-model.number="form.batchSize"
            type="number"
            :label="$t('listing_refresh.batch_size')"
            variant="outlined"
            density="compact"
          />
          <v-text-field
            v-model.number="form.rotationWeight"
            type="number"
            :label="$t('listing_refresh.rotation_weight')"
            variant="outlined"
            density="compact"
          />
          <v-text-field
            v-model="form.indexWindowHours"
            :label="$t('listing_refresh.index_window_hours')"
            :hint="$t('listing_refresh.index_window_hint', { days: detail.indexWindowDays })"
            persistent-hint
            variant="outlined"
            density="compact"
            class="mb-2"
          />
          <v-text-field
            v-model.number="form.maxPagesPerRun"
            type="number"
            :label="$t('listing_refresh.max_pages')"
            variant="outlined"
            density="compact"
          />
          <v-text-field
            v-model="form.indexCursor"
            :label="$t('listing_refresh.index_cursor')"
            :hint="$t('listing_refresh.index_cursor_hint')"
            persistent-hint
            variant="outlined"
            density="compact"
            class="mb-2"
          />
          <v-text-field
            v-model.number="form.indexPage"
            type="number"
            min="1"
            :label="$t('listing_refresh.index_page')"
            variant="outlined"
            density="compact"
          />
          <v-autocomplete
            v-model="form.titleCodes"
            :items="titles"
            :loading="loadingTitles"
            item-title="title"
            item-value="partCode"
            multiple
            chips
            closable-chips
            clearable
            :label="$t('listing_refresh.titles')"
            :hint="$t('listing_refresh.titles_hint')"
            persistent-hint
            variant="outlined"
            density="compact"
            class="mb-2"
          />
          <div class="d-flex justify-end">
            <v-btn color="red-darken-3" :loading="saving" @click="save">
              {{ $t("listing_refresh.save") }}
            </v-btn>
          </div>
        </v-card>

        <v-card class="pa-4">
          <div class="text-subtitle-1 mb-2">{{ $t("listing_refresh.indexer") }}</div>
          <div class="d-flex justify-space-between py-1">
            <span>{{ $t("listing_refresh.indexed_up_to") }}</span>
            <strong>{{ formatTime(detail.indexer.indexCursor) }}</strong>
          </div>
          <div class="d-flex justify-space-between py-1">
            <span>{{ $t("listing_refresh.next_page") }}</span>
            <strong>{{ detail.indexer.indexPage }}</strong>
          </div>
          <div class="d-flex justify-space-between py-1">
            <span>{{ $t("listing_refresh.cutoff") }}</span>
            <strong>{{ formatTime(detail.indexer.cutoff) }}</strong>
          </div>
          <div class="d-flex justify-space-between py-1">
            <span>{{ $t("listing_refresh.listings_indexed") }}</span>
            <strong>{{ detail.indexer.listings }}</strong>
          </div>
          <div class="d-flex justify-space-between py-1">
            <span>{{ $t("listing_refresh.orphans") }}</span>
            <strong>{{ detail.indexer.orphans }}</strong>
          </div>
          <div class="d-flex justify-space-between py-1">
            <span>{{ $t("listing_refresh.ambiguous") }}</span>
            <strong>{{ detail.indexer.ambiguous }}</strong>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import getRequest from "@/api/getRequest";
import putRequest from "@/api/putRequest";
import postRequest from "@/api/postRequest";

const OUTCOME_ORDER = [
  "DONE",
  "FALLBACK_RELISTED",
  "SKIPPED_ELIGIBILITY",
  "SKIPPED_NOT_SELLABLE",
  "ABORTED_SOLD",
  "END_PARTIAL_RESTORED",
  "OFF_EBAY_ATTENTION",
  "DRY_RUN",
];

export default {
  name: "ListingRefreshInstitution",
  data() {
    return {
      detail: null,
      form: {},
      titles: [],
      loadingTitles: false,
      error: "",
      saving: false,
      indexing: false,
      indexResult: "",
      outcomeRange: "allTime",
      outcomeOrder: OUTCOME_ORDER,
    };
  },
  computed: {
    doneRows() {
      return (this.detail && this.detail.queueRows.DONE) || 0;
    },
    waitingRows() {
      if (!this.detail) return 0;
      return (this.detail.queueRows.QUEUED || 0) + (this.detail.queueRows.IN_PROGRESS || 0);
    },
    skippedRows() {
      return (this.detail && this.detail.queueRows.SKIPPED) || 0;
    },
    progress() {
      const base = this.doneRows + this.waitingRows + this.skippedRows;
      if (!base) return 0;
      return Math.round((this.doneRows / base) * 100);
    },
    selectedOutcomes() {
      if (!this.detail) return {};
      return this.detail.outcomes[this.outcomeRange] || {};
    },
    chartMax() {
      if (!this.detail) return 1;
      return Math.max(1, ...this.detail.days.map((day) => day.refreshed + day.errors));
    },
    attemptHeaders() {
      return [
        { title: this.$t("listing_refresh.part"), key: "part" },
        { title: this.$t("listing_refresh.ended"), key: "oldItemIds" },
        { title: this.$t("listing_refresh.new_listings"), key: "newItemIds" },
        { title: this.$t("listing_refresh.result"), key: "status" },
        { title: this.$t("listing_refresh.time"), key: "createdAt" },
      ];
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.error = "";
      getRequest(`/listing-refresh/institutions/${this.$route.params.institutionId}`)
        .then((data) => {
          this.detail = data;
          this.form = {
            enabled: data.enabled,
            dryRun: data.dryRun,
            dailyCap: data.dailyCap,
            batchSize: data.batchSize,
            rotationWeight: data.rotationWeight,
            indexWindowHours: data.indexWindowHours == null ? "" : data.indexWindowHours,
            maxPagesPerRun: data.maxPagesPerRun,
            indexCursor: data.indexer.indexCursor
              ? new Date(data.indexer.indexCursor).toISOString()
              : "",
            indexPage: data.indexer.indexPage || 1,
            titleCodes: data.titleCodes || [],
          };
          if (!this.titles.length) this.loadTitles();
        })
        .catch((err) => {
          this.error = (err.response && err.response.data && err.response.data.errorMsg) || err.message;
        });
    },
    loadTitles() {
      this.loadingTitles = true;
      getRequest("/listing-refresh/titles")
        .then((data) => {
          this.titles = Array.isArray(data) ? data : [];
        })
        .finally(() => {
          this.loadingTitles = false;
        });
    },
    save() {
      this.saving = true;
      this.error = "";
      const body = {
        ...this.form,
        indexWindowHours: this.form.indexWindowHours === "" ? null : Number(this.form.indexWindowHours),
      };
      putRequest(`/listing-refresh/institutions/${this.$route.params.institutionId}`, "", body)
        .then(() => this.load())
        .catch((err) => {
          this.error = (err.response && err.response.data && err.response.data.errorMsg) || err.message;
        })
        .finally(() => {
          this.saving = false;
        });
    },
    setPaused(paused) {
      this.saving = true;
      putRequest(`/listing-refresh/institutions/${this.$route.params.institutionId}`, "", { paused })
        .then(() => this.load())
        .catch((err) => {
          this.error = (err.response && err.response.data && err.response.data.errorMsg) || err.message;
        })
        .finally(() => {
          this.saving = false;
        });
    },
    runIndex() {
      this.indexing = true;
      this.indexResult = "";
      this.error = "";
      postRequest
        .object(`/listing-refresh/institutions/${this.$route.params.institutionId}/index`, {})
        .then((res) => {
          const data = res.data || {};
          const counts = data.counts || {};
          this.indexResult = data.indexed === false
            ? data.reason
            : `${data.pagesRead || 0} pages, QUEUED ${counts.QUEUED || 0}, next page ${data.indexPage}`;
          this.load();
        })
        .catch((err) => {
          this.error = (err.response && err.response.data && err.response.data.errorMsg) || err.message;
        })
        .finally(() => {
          this.indexing = false;
        });
    },
    barHeight(value) {
      return Math.round((value / this.chartMax) * 80);
    },
    statusLabel(item) {
      if (item.status === "paused") {
        return `${this.$t("listing_refresh.paused")}${item.pauseReason ? ` · ${item.pauseReason}` : ""}`;
      }
      return this.$t(`listing_refresh.status_${item.status}`);
    },
    statusColor(status) {
      if (status === "live") return "success";
      if (status === "dry_run") return "warning";
      if (status === "paused") return "error";
      return "grey";
    },
    outcomeLabel(status) {
      const key = `listing_refresh.outcome_${status}`;
      const label = this.$t(key);
      return label === key ? status : label;
    },
    formatTime(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString();
    },
  },
};
</script>
